import React, { useState } from 'react';
import { useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import { ManagedReportItemCustomization } from '../Shared/ManagedReport';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminEditorMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
    SlimUserAdminModel,
    mapSlimUserAdminModel,
} from '../Shared/ManagedHelpers';
import {
    AddIcon,
    Button,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Input,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import GetUserAdminModelsRequest from '../../Interfaces/Requests/Administration/GetUserAdminModelsRequest';
import GetUserAdminModelsResponse from '../../Interfaces/Responses/Administration/GetUserAdminModelResponse';
import Administration from '../../Constants/Endpoints/Administration';

export type ManagedReportItemCustomizationEditResult = {
    operation: ManagedAdminChildOperation;
    reportItemCustomization: ManagedReportItemCustomization;
};

type AUDReportItemCustomizationFormValues = {
    ricUserID: number;
    ricColumnName: string;
    ricType: string;
    ricValue: number | null;
    ricValueStr: string;
};

type AUDReportItemCustomizationsProps = {
    reportID: number;
    reportName: string;
    reportGroupName: string;
    reportPageID: number;
    reportPageTitle: string;
    reportItemID: number;
    reportItemTitle: string;
    reportItemCustomizations: ManagedReportItemCustomization[];
    initialSelectedReportItemCustomizationID?: number;
    initialMode?: ManagedAdminActionMode;
    handleSetReportItemCustomizations: (
        result: ManagedReportItemCustomizationEditResult
    ) => void;
    close: () => void;
};

const AUDReportItemCustomizations: React.FC<AUDReportItemCustomizationsProps> = ({ close, ...props }) => {
    const [formAUDReportItemCustomization] = Form.useForm();

    const reportItemCustomizations = props.reportItemCustomizations ?? [];

    const getInitialRICMode = (): ManagedAdminActionMode => {
        switch (props.initialMode) {
            case 'update':
                return 'update';
            case 'delete':
                return 'delete';
            case 'add':
            default:
                return 'add';
        }
    };

    const getInitialSelectedReportItemCustomization = (): ManagedReportItemCustomization | undefined => {
        if (!props.initialSelectedReportItemCustomizationID || props.initialSelectedReportItemCustomizationID <= 0) {
            return undefined;
        }

        return reportItemCustomizations.find(
            (reportItemCustomization) => reportItemCustomization.id === props.initialSelectedReportItemCustomizationID
        );
    };

    const buildReportItemCustomizationNameConstruct = (ric: ManagedReportItemCustomization | undefined): string => {
        if (!ric) {
            return '';
        }

        return 'UserID: [' + ric.userID + '] / Type: [' + ric.type + '] / Column: [' + (ric.columnName ?? '') + ']';
    };

    const initialSelectedReportItemCustomization = getInitialSelectedReportItemCustomization();
    const initialRICMode = getInitialRICMode();

    const initialRICFormValues = initialRICMode === 'add'
        ? {
            ricUserID: 0,
            ricColumnName: '',
            ricType: '',
            ricValue: null,
            ricValueStr: '',
        }
        : {
            ricUserID: initialSelectedReportItemCustomization?.userID ?? 0,
            ricColumnName: initialSelectedReportItemCustomization?.columnName ?? '',
            ricType: initialSelectedReportItemCustomization?.type ?? '',
            ricValue: initialSelectedReportItemCustomization?.value ?? null,
            ricValueStr: initialSelectedReportItemCustomization?.valueStr ?? '',
        };

    const [addButtonClicked, setAddButtonClicked] = useState(initialRICMode === 'add');
    const [updButtonClicked, setUpdButtonClicked] = useState(initialRICMode === 'update');
    const [delButtonClicked, setDelButtonClicked] = useState(initialRICMode === 'delete');

    const ricIncludeDisabled = false;

    const formatRICDisplayID = (id?: number | null): string => {
        return id && id > 0 ? String(id) : 'new';
    };

    const ricPropsReportID = props.reportID > 0 ? props.reportID : 0;
    const ricPropsReportName = props.reportName !== '' ? props.reportName : 'New Report';
    const ricPropsReportGroupName = props.reportGroupName !== '' ? props.reportGroupName : 'New Report Group';
    const ricPropsReportPageID = props.reportPageID > 0 ? props.reportPageID : 0;
    const ricPropsReportPageTitle = props.reportPageTitle !== '' ? props.reportPageTitle : 'New Report Page';
    const ricPropsReportItemID = props.reportItemID > 0 ? props.reportItemID : 0;
    const ricPropsReportItemTitle = props.reportItemTitle !== '' ? props.reportItemTitle : 'New Report Item';

    const [ricSelectedReportItemCustomizationID, setRICSelectedReportItemCustomizationID] = useState(initialSelectedReportItemCustomization?.id ?? -1);
    const [ricSelectedReportItemCustomizationTitle, setRICSelectedReportItemCustomizationTitle] = useState(
        buildReportItemCustomizationNameConstruct(initialSelectedReportItemCustomization)
    );

    const [ricUserID, setRICUserID] = useState<number>(initialSelectedReportItemCustomization?.userID ?? 0);
    const [ricType, setRICType] = useState<string>(initialSelectedReportItemCustomization?.type ?? '');

    const [managedActionModalState, setManagedActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const validateRICDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'ricUserID': {
                const newRICUserID = formAUDReportItemCustomization.getFieldValue('ricUserID');

                return (!newRICUserID || newRICUserID === 0)
                    ? Promise.reject()
                    : Promise.resolve();
            }

            case 'ricColumnName':
            case 'ricValue':
            case 'ricValueStr':
                return Promise.resolve();

            case 'ricType': {
                const newRICType = formAUDReportItemCustomization.getFieldValue('ricType');

                return (!newRICType || newRICType.trim().length === 0)
                    ? Promise.reject()
                    : Promise.resolve();
            }

            default:
                return Promise.reject();
        }
    };

    const handleRICLoadError = (title: string, err: unknown) => {
        console.error(title + ': ', err);

        const apiError = err as ManagedApiError;

        const message = apiError?.response?.data?.message
            ? `${title}: ${apiError.response.data.message}`
            : `${title}: ${String(err)}`;

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Report Item Customization Load Error',
                message,
                true
            )
        );
    };

    const { data: users = [], isLoading: isGetUsersLoading } = useQuery<GetUserAdminModelsResponse[], Error, SlimUserAdminModel[]>(
        ['getUserAdminModelsRequest', ricIncludeDisabled],
        async (): Promise<GetUserAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_USER_ADMIN_MODELS}`;
                const requestData: GetUserAdminModelsRequest = { gua_includeDisabled: ricIncludeDisabled };
                return await AxiosUtil.get<GetUserAdminModelsResponse[], GetUserAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleRICLoadError('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleRICLoadError('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error', err);
            },
            select: (response: GetUserAdminModelsResponse[]): SlimUserAdminModel[] => {
                return response
                    .map(mapSlimUserAdminModel)
                    .filter((eachUser) => !eachUser.deleted)
                    .sort((a, b) =>
                        (a.lastName + ', ' + a.firstName).localeCompare(b.lastName + ', ' + b.firstName)
                    );
            },
        }
    );

    const handleRICUserIDChange = (value: number) => {
        setRICUserID(value);
        formAUDReportItemCustomization.setFieldValue('ricUserID', value);
    };

    const handleRICTypeChange = (value: string) => {
        setRICType(value);
        formAUDReportItemCustomization.setFieldValue('ricType', value);
    };

    const onFinish = (values: AUDReportItemCustomizationFormValues) => {
        const newManagedReportItemCustomization: ManagedReportItemCustomization = {
            id: addButtonClicked ? 0 : (updButtonClicked ? ricSelectedReportItemCustomizationID : (delButtonClicked ? ricSelectedReportItemCustomizationID : 0)),
            reportItemID: ricPropsReportItemID,
            userID: values.ricUserID,
            columnName: values.ricColumnName,
            type: values.ricType,
            value: values.ricValue,
            valueStr: values.ricValueStr,
        };

        props.handleSetReportItemCustomizations({
            operation: getRICOperation(),
            reportItemCustomization: newManagedReportItemCustomization,
        });

        close();
    };

    const setRICMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const clearRICSelection = () => {
        setRICSelectedReportItemCustomizationID(-1);
        setRICSelectedReportItemCustomizationTitle('');

        setRICMode('add');
        handleRICReportItemCustomizationResetForm('blank');
    };

    const handleRICSelectionChange = (e: number) => {
        const selectedReportItemCustomization = reportItemCustomizations.find(
            (reportItemCustomization) => reportItemCustomization.id === e
        );

        setRICSelectedReportItemCustomizationID(e);
        setRICSelectedReportItemCustomizationTitle(
            buildReportItemCustomizationNameConstruct(selectedReportItemCustomization)
        );

        setRICMode('add');
        handleRICReportItemCustomizationResetForm('blank');
    };

    const getRICOperation = (): ManagedAdminChildOperation => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        return 'delete';
    };

    const getRICActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const setRICBlankForm = () => {
        setRICUserID(0);
        setRICType('');

        formAUDReportItemCustomization.setFieldValue('ricUserID', 0);
        formAUDReportItemCustomization.setFieldValue('ricColumnName', '');
        formAUDReportItemCustomization.setFieldValue('ricType', '');
        formAUDReportItemCustomization.setFieldValue('ricValue', null);
        formAUDReportItemCustomization.setFieldValue('ricValueStr', '');
    };

    const setRICFormFromSelectedReportItemCustomization = () => {
        const selectedReportItemCustomization = reportItemCustomizations.find(
            (reportItemCustomization) => reportItemCustomization.id === ricSelectedReportItemCustomizationID
        );

        const selectedUserID = selectedReportItemCustomization?.userID ?? 0;
        const selectedType = selectedReportItemCustomization?.type ?? '';

        setRICUserID(selectedUserID);
        setRICType(selectedType);

        formAUDReportItemCustomization.setFieldValue('ricUserID', selectedUserID);
        formAUDReportItemCustomization.setFieldValue('ricColumnName', selectedReportItemCustomization?.columnName ?? '');
        formAUDReportItemCustomization.setFieldValue('ricType', selectedType);
        formAUDReportItemCustomization.setFieldValue('ricValue', selectedReportItemCustomization?.value ?? null);
        formAUDReportItemCustomization.setFieldValue('ricValueStr', selectedReportItemCustomization?.valueStr ?? '');
    };

    const handleRICReportItemCustomizationResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setRICBlankForm();
            return;
        }

        setRICFormFromSelectedReportItemCustomization();
    };

    //=================================================================
    //=================================================================
    
    const handleRICAddReportItemCustomization = () => {
        clearRICSelection();
    };

    //=================================================================
    //=================================================================

    const handleRICUpdReportItemCustomization = () => {
        setRICMode('update');
        handleRICReportItemCustomizationResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleRICDelReportItemCustomization = () => {
        setRICMode('delete');
        handleRICReportItemCustomizationResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const onRICCancel = () => {
        clearRICSelection();
    };

    const handleCloseManagedActionModal = () => {
        setManagedActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const onFinishFailed = (errorInfo: ManagedFormErrorInfo<AUDReportItemCustomizationFormValues>) => {
        console.error('AUDReportItemCustomizations validation failed:', errorInfo);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Report Item Customization Validation',
                'Please correct the highlighted Report Item Customization fields and try again.',
                true
            )
        );
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '16px',
        paddingRight: '8px',
        paddingLeft: '8px',
        height: '375px'
    };

    return (
        <>
            <ManagedActionModal
                modalState={managedActionModalState}
                onOk={handleCloseManagedActionModal}
            />
            <Spin spinning={isGetUsersLoading}>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Report Item Customizations</Title>
                    </Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report: [' + formatRICDisplayID(ricPropsReportID) + '] ' + ricPropsReportGroupName + '-' + ricPropsReportName}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report Page: [' + formatRICDisplayID(ricPropsReportPageID) + '] ' + ricPropsReportPageTitle}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report Item: [' + formatRICDisplayID(ricPropsReportItemID) + '] ' + ricPropsReportItemTitle}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report Item Customization: [' + formatRICDisplayID(ricSelectedReportItemCustomizationID) + '] ' + ricSelectedReportItemCustomizationTitle}</Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{float: 'right'}}>Select Report Item Customization to Update or Delete:</Text>
                    </Col>
                    <Col span={14}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a Report Item Customization'}
                            showSearch
                            style={{paddingTop: '4px', width: '95%'}}
                            value={ricSelectedReportItemCustomizationID === -1 ? undefined : ricSelectedReportItemCustomizationID}
                            onChange={handleRICSelectionChange}
                        >
                            {reportItemCustomizations.map(
                                (eachReportItemCustomization) => {
                                    return (
                                        <Option
                                            key={eachReportItemCustomization.id}
                                            label={buildReportItemCustomizationNameConstruct(eachReportItemCustomization)}
                                            value={eachReportItemCustomization.id}
                                        >
                                            {buildReportItemCustomizationNameConstruct(eachReportItemCustomization)}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={6}>
                        <Button
                            icon={<AddIcon />}
                            style={{ float: 'right' , marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRICAddReportItemCustomization}
                        >
                            Add Report Item Customization
                        </Button>
                    </Col>
                    <Col span={6} />
                    <Col span={6}>
                        <Button
                            disabled={ricSelectedReportItemCustomizationID === -1}
                            icon={<EditIcon />}
                            style={{ backgroundColor: 'darkgray', float: 'right', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRICUpdReportItemCustomization}
                        >
                            Update Report Item Customization
                        </Button>
                    </Col>
                    <Col span={6}>
                        <Button
                            disabled={ricSelectedReportItemCustomizationID === -1}
                            icon={<DeleteIcon />}
                            style={{ backgroundColor: 'red', float: 'right', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRICDelReportItemCustomization}
                        >
                            Delete Report Item Customization
                        </Button>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Form
                    form={formAUDReportItemCustomization}
                    initialValues={initialRICFormValues}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Divider orientation='left' orientationMargin={30} variant='dotted' style={{ borderColor: 'black', color: getRICActionColor() }}>
                                    {addButtonClicked ? 'Add Report Item Customization' :
                                        (updButtonClicked ? 'Update Report Item Customization: [' + formatRICDisplayID(ricSelectedReportItemCustomizationID) + '] ' :
                                            (delButtonClicked ? 'Delete Report Item Customization: [' + formatRICDisplayID(ricSelectedReportItemCustomizationID) + '] ' : ''))}
                                </Divider>
                            </Col>
                            <Col span={3}>
                                <Button
                                    disabled={delButtonClicked}
                                    style={{ color: getRICActionColor(), fontStyle: 'italic' }}
                                    type='link'
                                    onClick={() => handleRICReportItemCustomizationResetForm(addButtonClicked ? 'blank' : 'selected')}
                                >
                                    Reset Form
                                </Button>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={6} />
                            <Col span={12}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='ricUserID'
                                        rules={[
                                            {
                                                message: 'User ID required.',
                                                required: true,
                                                validator: validateRICDetailsData,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                            label='User ID:'
                                            optionFilterProp='label'
                                            showSearch
                                            value={ricUserID}
                                            onChange={handleRICUserIDChange}
                                        >
                                            <Option
                                                value={0}
                                                label={'(Not Set)'}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            {users.map(
                                                (eachUser) => {
                                                    return (
                                                        <Option
                                                            key={eachUser.id}
                                                            label={eachUser.firstName + ' ' + eachUser.lastName}
                                                            value={eachUser.id}
                                                        >
                                                            {eachUser.firstName + ' ' + eachUser.lastName}
                                                        </Option>
                                                    );
                                                }
                                            )}
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={6} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={11}>
                                <Form.Item
                                    key='riccolumnname'
                                    name='ricColumnName'
                                    label='Column Name'
                                    labelCol={{ span: 5, }}
                                    rules={[
                                        {
                                            validator: validateRICDetailsData,
                                        },
                                    ]}
                                    style={{marginBottom: '4px', marginTop: '26px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Column Name'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={2} />
                            <Col span={11}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='ricType'
                                        rules={[
                                            {
                                                message: 'Type required.',
                                                required: true,
                                                validator: validateRICDetailsData,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            label='Type:'
                                            value={ricType}
                                            showSearch
                                            onChange={handleRICTypeChange}
                                        >
                                            <Option
                                                value={'alignment'}
                                                label={'Alignment'}
                                            >
                                                {'Alignment'}
                                            </Option>
                                            <Option
                                                value={'columnTitle'}
                                                label={'Column Title'}
                                            >
                                                {'Column Title'}
                                            </Option>
                                            <Option
                                                value={'format'}
                                                label={'Format'}
                                            >
                                                {'Format'}
                                            </Option>
                                            <Option
                                                value={'title'}
                                                label={'Title'}
                                            >
                                                {'Title'}
                                            </Option>
                                            <Option
                                                value={'visibility'}
                                                label={'Visibility'}
                                            >
                                                {'Visibility'}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={11}>
                                <Form.Item
                                    key='ricvalue'
                                    name='ricValue'
                                    label='Value'
                                    labelCol={{ span: 5, }}
                                    rules={[
                                        {
                                            validator: validateRICDetailsData,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Value'
                                        style={{color: delButtonClicked ? 'gray' : 'black', float: 'left', width: '40%'}}
                                        type='number'
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={2} />
                            <Col span={11}>
                                <Form.Item
                                    key='ricvaluestr'
                                    name='ricValueStr'
                                    label='Value String'
                                    labelCol={{ span: 4, }}
                                    rules={[
                                        {
                                            validator: validateRICDetailsData,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Value String'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={6}>
                            <Form.Item
                                name='ricCloseButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={close}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={6} />
                        <Col span={6}>
                            <Form.Item
                                name='ricApplyButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    htmlType='submit'
                                    style={{ backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')), float: 'right', width: '95%' }}
                                    type='primary'
                                >
                                    {addButtonClicked ? 'Add Report Item Customization' : (updButtonClicked ? 'Update Report Item Customization' : (delButtonClicked ? 'Delete Report Item Customization' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name='ricCancelButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={onRICCancel}
                                >
                                    {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AUDReportItemCustomizations;
